import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';

// Create context with default value
const StatusTrackingContext = createContext({
  bookProcessingStatuses: {},
  updateBookProcessingStatus: (bookId, status) => {},
  clearBookProcessingStatus: () => {}
});

// Custom hook to use the context
export const useStatusTracking = () => {
  const context = useContext(StatusTrackingContext);
  if (!context) {
    throw new Error('useStatusTracking must be used within StatusTrackingProvider');
  }
  return context;
};

export function StatusTrackingProvider({ children }) {
  // Track active book processing statuses by bookId
  const [bookProcessingStatuses, setBookProcessingStatuses] = useState({});
  
  // Function to update a book's processing status
  const updateBookProcessingStatus = (bookId, status) => {
    console.log(`StatusTracking: Book ${bookId} status update to ${status}`);
    
    setBookProcessingStatuses(prev => ({
      ...prev,
      [bookId]: status
    }));
    
    // Show appropriate toast based on status
    const messages = {
      initializing: "Starting to generate your book preview...",
      savingTextElements: "Saving your text elements...",
      // generatingCharacter is handled separately on World page - don't show here
      // generatingCharacter: "Design is ready! You can continue while the remaining image prompts are being generated.",
      generatingImagePrompts: "Generating image prompts for your story...",
      generatingCoverImage: "Creating your book cover image...",
      updatingImageElements: "Adding images to your book...",
      completed: "Book preview completed successfully!"
    };
    
    if (messages[status]) {
      console.log(`🍞 SHOWING TOAST FOR STATUS: ${status}`, messages[status]);
      
      // More prominent toast with longer display time
      toast.success(messages[status], {
        position: "top-right",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        toastId: `book-status-${bookId}-${status}`, // Prevent duplicates
        onOpen: () => console.log(`Toast opened for status: ${status}`)
      });
    }
  };
  
  // Clear a book's status tracking
  const clearBookProcessingStatus = (bookId) => {
    setBookProcessingStatuses(prev => {
      const newState = { ...prev };
      delete newState[bookId];
      return newState;
    });
  };
  
  const value = {
    bookProcessingStatuses,
    updateBookProcessingStatus,
    clearBookProcessingStatus
  };

  return (
    <StatusTrackingContext.Provider value={value}>
      {children}
    </StatusTrackingContext.Provider>
  );
}
